/**
 * Delivery state of customer email (#3787): the Resend rows behind
 * GET /outbound-emails, for one document or one customer.
 *
 * The status, entity types and row shape mirror the API
 * (services/staff-api lib/email-delivery.ts) and are declared here so the
 * portal bundle never pulls server code.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { useCollectionSync } from './useEntitySync';

export type OutboundEmailStatus =
  | 'sent'
  | 'delayed'
  | 'delivered'
  | 'bounced'
  | 'complained'
  | 'failed'
  | 'suppressed';

/** A document the email carried, or a customer whose addresses the server resolves. */
export type OutboundEmailSubjectType =
  | 'invoice'
  | 'receipt'
  | 'quote'
  | 'work_order_offer'
  | 'booking'
  | 'inspection_report'
  | 'person'
  | 'company';

export interface OutboundEmail {
  id: string;
  entityType: string | null;
  entityId: string | null;
  /** A status a newer server adds reads as unknown, not as delivered. */
  status: OutboundEmailStatus | (string & {});
  recipientEmail: string;
  subject: string | null;
  statusReason: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  lastEventAt: string;
}

export interface OutboundEmailsResponse {
  emails: OutboundEmail[];
  hasMore: boolean;
  limit: number;
  offset: number;
}

/**
 * The API's own bounds: a page is at most 50 rows and `offset` at most 10000.
 * The portal reads small pages one at a time, so older rows stay reachable
 * without any single request growing.
 */
export const OUTBOUND_EMAILS_PAGE_SIZE = 10;
export const OUTBOUND_EMAILS_MAX_LIMIT = 50;
export const OUTBOUND_EMAILS_MAX_OFFSET = 10000;

const SYNC_OPTIONS = {
  relevantEntityTypes: ['outbound_emails'],
  invalidationKeys: [{ queryKey: queryKeys.outboundEmails.all }],
};

/**
 * Deliveries of one subject, newest first, one page per `fetchNextPage`. The
 * Resend webhook announces each new or changed row on the `outbound_emails`
 * collection room; the event does not say which document it belongs to, so
 * every delivery query refetches its loaded pages. A new row shifts the older
 * ones down by one, so a row can land on two neighbouring pages: `emails`
 * holds each id once.
 */
export function useOutboundEmails(
  entityType: OutboundEmailSubjectType,
  entityId: string | undefined,
  pageSize: number = OUTBOUND_EMAILS_PAGE_SIZE,
) {
  useCollectionSync('outbound_emails', SYNC_OPTIONS);
  return useInfiniteQuery({
    queryKey: queryKeys.outboundEmails.list(entityType, entityId ?? '', pageSize),
    queryFn: ({ pageParam }) =>
      api.get<OutboundEmailsResponse>('/outbound-emails', {
        params: { entityType, entityId: entityId ?? '', limit: pageSize, offset: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const next = allPages.length * pageSize;
      return lastPage.hasMore && next <= OUTBOUND_EMAILS_MAX_OFFSET ? next : undefined;
    },
    enabled: !!entityId,
    select: (data) => {
      const seen = new Set<string>();
      const emails = data.pages
        .flatMap((page) => page.emails)
        .filter((email) => (seen.has(email.id) ? false : (seen.add(email.id), true)));
      return { emails };
    },
  });
}
